import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { InvoiceV2Service } from '../../../services/invoice-v2.service';
import {
  computeProcedureActionFlags,
  getLocalizedApiMessage,
  getProcedureLabel,
  normalizeProcedureInvoiceRow,
  ProcedureActionFlags,
} from '../invoice-v2-procedure.util';
import {
  confirmHearingRequestSwal,
  promptGoToTaxPortalSwal,
} from '../hearing-session-swal.util';

type ProcedureOptionId =
  | 'cancel'
  | 'continue'
  | 'reverse_charge'
  | 'hearing'
  | 'after_hearing';

interface ProcedureOption {
  id: ProcedureOptionId;
  titleKey: string;
  descriptionKey: string;
}

const DEFAULT_HEARING_URL = 'https://secapp.taxes.gov.il/em-hkz-hsb-intr';

/** Legacy allocation-denial order for initial procedure cycle. */
const INITIAL_CYCLE_ORDER: ProcedureOptionId[] = [
  'cancel',
  'continue',
  'reverse_charge',
  'hearing',
];

@Component({
  selector: 'app-invoices-v2-procedure-panel',
  templateUrl: './invoices-v2-procedure-panel.component.html',
  styleUrls: ['./invoices-v2-procedure-panel.component.scss'],
})
export class InvoicesV2ProcedurePanelComponent implements OnChanges {
  @Input() invoice: Record<string, unknown> | null = null;
  @Input() invoiceId!: number;
  @Input() curentLang = 'en';
  @Output() completed = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  flags: ProcedureActionFlags = computeProcedureActionFlags(null);
  availableOptions: ProcedureOption[] = [];
  selectedOption: ProcedureOptionId | '' = '';
  isSubmitting = false;

  private normalizedInvoice: Record<string, unknown> | null = null;

  constructor(
    private invoiceV2Service: InvoiceV2Service,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['invoice']) {
      this.normalizedInvoice = this.invoice
        ? normalizeProcedureInvoiceRow(this.invoice)
        : null;
      this.flags = computeProcedureActionFlags(this.normalizedInvoice);
      this.availableOptions = this.buildAvailableOptions();
      this.selectedOption = '';
    }
  }

  get procedureLabel(): string {
    return getProcedureLabel(
      this.normalizedInvoice?.['procedure'] as number | null | undefined,
      this.curentLang,
    );
  }

  get hasChosenProcedure(): boolean {
    const proc = this.normalizedInvoice?.['procedure'];
    return proc != null && proc !== '';
  }

  selectOption(option: ProcedureOptionId): void {
    this.selectedOption = option;
  }

  onNotDecided(): void {
    this.selectedOption = '';
    this.closed.emit();
  }

  onContinue(): void {
    if (!this.selectedOption || this.isSubmitting) return;

    switch (this.selectedOption) {
      case 'cancel':
        this.cancelInvoice();
        break;
      case 'continue':
        this.continueInvoice();
        break;
      case 'reverse_charge':
        this.reverseCharge();
        break;
      case 'hearing':
        this.confirmAndFurtherObjection();
        break;
      case 'after_hearing':
        this.afterHearingApproval();
        break;
    }
  }

  private buildAvailableOptions(): ProcedureOption[] {
    const byId = new Map<ProcedureOptionId, ProcedureOption>();

    if (this.flags.canCancel) {
      byId.set('cancel', {
        id: 'cancel',
        titleKey: 'allocationDenial.options.cancel.title',
        descriptionKey: 'allocationDenial.options.cancel.description',
      });
    }
    if (this.flags.canContinue) {
      byId.set('continue', {
        id: 'continue',
        titleKey: 'allocationDenial.options.continueWithout.title',
        descriptionKey: 'allocationDenial.options.continueWithout.description',
      });
    }
    if (this.flags.canReverseCharge) {
      byId.set('reverse_charge', {
        id: 'reverse_charge',
        titleKey: 'allocationDenial.options.reverseCharge.title',
        descriptionKey: 'allocationDenial.options.reverseCharge.description',
      });
    }
    if (this.flags.canFurtherObjection) {
      byId.set('hearing', {
        id: 'hearing',
        titleKey: 'allocationDenial.options.transferUnit.title',
        descriptionKey: 'allocationDenial.options.transferUnit.description',
      });
    }
    if (this.flags.canAfterHearingApproval) {
      byId.set('after_hearing', {
        id: 'after_hearing',
        titleKey: 'invoices_v2.procedure.after_hearing_title',
        descriptionKey: 'invoices_v2.procedure.after_hearing_description',
      });
    }

    const ordered: ProcedureOption[] = [];
    for (const id of INITIAL_CYCLE_ORDER) {
      const opt = byId.get(id);
      if (opt) {
        ordered.push(opt);
        byId.delete(id);
      }
    }
    const afterHearing = byId.get('after_hearing');
    if (afterHearing) {
      ordered.push(afterHearing);
      byId.delete('after_hearing');
    }
    for (const opt of byId.values()) {
      ordered.push(opt);
    }

    return ordered;
  }

  private handleTaxResponse(
    response: { status?: number; link?: string; data?: { zeroTaxInvoice?: { invoice_id?: number } } },
    options?: { isHearingAction?: boolean; isReverseCharge?: boolean },
  ): void {
    if (response?.status === 200) {
      const message = getLocalizedApiMessage(
        response,
        this.curentLang,
        this.translate.instant('invoices_v2.procedure.action_success'),
      );

      if (options?.isHearingAction) {
        void promptGoToTaxPortalSwal(
          this.curentLang,
          response.link || DEFAULT_HEARING_URL,
        );
      } else {
        this.toastr.success(message);
      }

      if (options?.isReverseCharge) {
        const newId = Number(response?.data?.zeroTaxInvoice?.invoice_id);
        if (Number.isFinite(newId) && newId > 0) {
          this.router.navigate(['/dashboard/invoices-v2/view'], {
            queryParams: { invoiceId: newId },
            state: { invoiceId: newId },
          });
        }
      }

      this.completed.emit();
    } else {
      this.toastr.warning(
        getLocalizedApiMessage(
          response,
          this.curentLang,
          this.translate.instant('invoices_v2.procedure.action_warning'),
        ),
      );
    }
  }

  private runAction(
    observableFactory: () => ReturnType<InvoiceV2Service['cancelInvoice']>,
    options?: { isHearingAction?: boolean; isReverseCharge?: boolean },
  ): void {
    this.isSubmitting = true;
    this.spinner.show();
    observableFactory().subscribe({
      next: (res) => {
        this.spinner.hide();
        this.isSubmitting = false;
        this.handleTaxResponse(res, options);
      },
      error: (err) => {
        this.spinner.hide();
        this.isSubmitting = false;
        this.toastr.error(
          getLocalizedApiMessage(
            err?.error,
            this.curentLang,
            this.translate.instant('invoices_v2.procedure.action_error'),
          ),
        );
      },
    });
  }

  cancelInvoice(): void {
    this.runAction(() => this.invoiceV2Service.cancelInvoice(this.invoiceId));
  }

  continueInvoice(): void {
    this.runAction(() => this.invoiceV2Service.continueInvoice(this.invoiceId));
  }

  private confirmAndFurtherObjection(): void {
    void confirmHearingRequestSwal(this.curentLang).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.runAction(() => this.invoiceV2Service.furtherObjection(this.invoiceId), {
        isHearingAction: true,
      });
    });
  }

  afterHearingApproval(): void {
    this.runAction(() => this.invoiceV2Service.afterHearingApproval(this.invoiceId));
  }

  reverseCharge(): void {
    this.runAction(() => this.invoiceV2Service.reverseCharge(this.invoiceId), {
      isReverseCharge: true,
    });
  }
}
